import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, Flame, MessageCircle, TrendingUp, Users, Zap, CreditCard, Eye } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const HeroSection = () => {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden pt-20">
      {/* Background effects */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] rounded-full bg-primary/5 blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full bg-accent/5 blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 md:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <span className="tag-glow text-warmz-orange mb-6">
              ✨ Aquecimento Completo: Mensagens, Stories e Grupos
            </span>

            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.1] tracking-tight text-foreground">
              Aqueça os seus números de WhatsApp no{" "}
              <span className="gradient-text-fire">piloto automático.</span>
            </h1>

            <p className="mt-6 text-lg text-muted-foreground max-w-xl leading-relaxed">
              Mensagens, áudios, stickers, publicação de Stories e participação em grupos de aquecimento —
              tudo 100% automático. Esteira de fases evolui cada chip de Cold até Mature sem intervenção.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-4 items-start">
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-primary text-primary-foreground font-bold text-base glow-orange transition-transform hover:scale-105"
              >
                <Shield size={20} />
                Testar Grátis por 7 Dias
              </Link>
            </div>

            {/* Trust badges */}
            <div className="mt-5 flex flex-wrap gap-2">
              <Badge variant="secondary" className="gap-1.5 px-3 py-1.5 text-xs bg-secondary/80 border border-warmz-glass-border/30">
                <Zap size={12} className="text-warmz-green-bright" />
                7 Dias Grátis
              </Badge>
              <Badge variant="secondary" className="gap-1.5 px-3 py-1.5 text-xs bg-secondary/80 border border-warmz-glass-border/30">
                <Users size={12} className="text-primary" />
                2 Números Inclusos (até 5 no trial)
              </Badge>
              <Badge variant="secondary" className="gap-1.5 px-3 py-1.5 text-xs bg-secondary/80 border border-warmz-glass-border/30">
                <CreditCard size={12} className="text-muted-foreground" />
                Cancele Quando Quiser
              </Badge>
            </div>
          </motion.div>

          {/* Right – Dashboard mockup */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="hidden lg:flex justify-center"
          >
            <div className="relative w-[420px] h-[440px]">
              <div className="glass-card gradient-border p-6 w-full h-full flex flex-col justify-between">
                {/* Top bar */}
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-3 h-3 rounded-full bg-destructive/80" />
                  <div className="w-3 h-3 rounded-full bg-warmz-orange/60" />
                  <div className="w-3 h-3 rounded-full bg-warmz-green/60" />
                  <span className="ml-3 text-xs text-muted-foreground font-mono">warmz-dashboard</span>
                </div>

                {/* Chat bubbles */}
                <div className="space-y-2.5 flex-1">
                  <div className="flex justify-end">
                    <div className="bg-accent/20 text-accent-foreground px-4 py-2 rounded-2xl rounded-br-md text-sm max-w-[200px]">
                      Olá, tudo bem? 👋
                    </div>
                  </div>
                  <div className="flex justify-start">
                    <div className="bg-secondary px-4 py-2 rounded-2xl rounded-bl-md text-sm max-w-[220px] text-secondary-foreground">
                      Bom dia! Tudo ótimo 🎉
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <div className="bg-accent/20 text-accent-foreground px-4 py-2 rounded-2xl rounded-br-md text-sm max-w-[200px]">
                      🎙️ Áudio (0:12)
                    </div>
                  </div>
                  <div className="flex justify-start">
                    <div className="bg-secondary px-4 py-2 rounded-2xl rounded-bl-md text-sm text-secondary-foreground">
                      😂 Sticker enviado
                    </div>
                  </div>
                  {/* Story indicator */}
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary/10 border border-primary/20">
                    <Eye className="w-3.5 h-3.5 text-primary" />
                    <span className="text-[11px] text-primary font-medium">Story publicado automaticamente</span>
                  </div>
                </div>

                {/* Phase indicator */}
                <div className="mt-4 p-3 rounded-lg bg-secondary/60 border border-warmz-glass-border/20">
                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
                    <span className="font-semibold text-foreground">Esteira de Fases</span>
                    <span className="text-warmz-green-bright font-mono">Dia 12</span>
                  </div>
                  <div className="flex items-center gap-1">
                    {["Cold", "Warm", "Hot", "Mature"].map((phase, idx) => (
                      <div key={phase} className="flex-1 flex flex-col items-center gap-1">
                        <div
                          className={`h-1.5 w-full rounded-full ${
                            idx === 0
                              ? "bg-blue-500"
                              : idx === 1
                              ? "bg-primary"
                              : "bg-secondary/80"
                          }`}
                        />
                        <span className={`text-[10px] ${idx <= 1 ? "text-foreground" : "text-muted-foreground/50"}`}>
                          {phase}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Status bar */}
                <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
                  <span className="flex items-center gap-1 text-warmz-green">
                    <span className="w-2 h-2 rounded-full bg-warmz-green-bright inline-block animate-pulse" />
                    Aquecimento ativo
                  </span>
                  <span>24/7</span>
                </div>
              </div>

              {/* Floating icons */}
              <motion.div
                animate={{ y: [0, -15, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -top-6 -right-6 w-14 h-14 rounded-xl bg-primary/20 border border-primary/30 flex items-center justify-center"
              >
                <Flame className="text-primary w-6 h-6" />
              </motion.div>
              <motion.div
                animate={{ y: [0, -12, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                className="absolute -bottom-4 -left-4 w-12 h-12 rounded-xl bg-accent/20 border border-accent/30 flex items-center justify-center"
              >
                <MessageCircle className="text-accent w-5 h-5" />
              </motion.div>
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 2 }}
                className="absolute top-1/2 -right-10 w-10 h-10 rounded-full bg-warmz-green/10 border border-warmz-green/20 flex items-center justify-center"
              >
                <TrendingUp className="text-warmz-green-bright w-4 h-4" />
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
